/**
 * asteroids — belts as INSTANCED fields (report §9 R10).
 *
 * Every rock was a full clone of one GLB (598 objects, a draw call each per sub-mesh, 598 identical silhouettes).
 * A field is now one InstancedMesh per master sub-mesh, count-packed to the field's rocks, with per-instance
 * VARIETY instead of copies: a hashed base orientation, a NON-UNIFORM scale (each axis 0.72–1.3 of the rock's
 * size — a potato, a slab, a shard from one mesh), a slow tumble about a hashed axis, and a per-instance albedo
 * shade (darker basalt to paler regolith; ice fields keep their tint). The material is the master's own (shared)
 * with static motion vectors pinned (the frozen-instance-motion-vector law — the tumble is slower than a pixel).
 *
 * Offset-local like every sector object: near fields (≤ SPIN_M) rewrite their matrices each frame from ABSOLUTE
 * positions minus the floating origin (and hold the mesh at the root's origin); far fields are left alone and ride
 * the rebase with the root like any child. Colliders stay the caller's (SpaceModule's collider groups).
 */
import * as THREE from 'three/webgpu';
import { pinStaticMotionVectors } from 'enginex/render/batching/staticMotionVectors.js';
import { fnv1a, rockSeat } from './space_look.js';

const SPIN_M = 40_000;
export const rockFieldState = { fields: 0, rocks: 0, meshes: 0, spinning: 0 };

const h01 = (s: string): number => fnv1a(s) / 4294967296;

interface Rock { x: number; y: number; z: number; s: THREE.Vector3; q: THREE.Quaternion; axis: THREE.Vector3; rate: number }

export class RockField {
  readonly group = new THREE.Group();
  private readonly meshes: Array<{ mesh: THREE.InstancedMesh; local: THREE.Matrix4 }> = [];
  private readonly rocks: Rock[] = [];
  private readonly centre = new THREE.Vector3();
  private radius = 0;
  private readonly _m = new THREE.Matrix4(); private readonly _r = new THREE.Matrix4(); private readonly _p = new THREE.Vector3(); private readonly _dq = new THREE.Quaternion();

  /**
   * `master` — the cooked rock (its meshes become the instanced parts); `items` — ABSOLUTE positions + base scale;
   * `key` — the field's hash salt; `mat` — an override material (the ice fields), else each part keeps its own.
   */
  constructor(master: THREE.Object3D, items: ReadonlyArray<{ pos: readonly [number, number, number]; scale: number }>, key: string, off: THREE.Vector3, mat?: THREE.Material) {
    this.group.name = `rock-field:${key}`;
    master.updateMatrixWorld(true);
    const inv = master.matrixWorld.clone().invert();
    const parts: Array<{ geo: THREE.BufferGeometry; mat: THREE.Material; local: THREE.Matrix4 }> = [];
    master.traverse((o) => {
      const m = o as THREE.Mesh;
      if (!m.isMesh || !m.geometry) return;
      parts.push({ geo: m.geometry, mat: mat ?? (m.material as THREE.Material), local: new THREE.Matrix4().multiplyMatrices(inv, m.matrixWorld) });
    });
    items.forEach((it, i) => {
      // THE HASH LAW: bare FNV-1a over keys that differ in their last character gave every rock the master's own
      // proportions, one family of poses and the same (1,1,1) spin axis — `rockSeat` runs the finalizer.
      const seat = rockSeat(key, i);
      const s = new THREE.Vector3(seat.scale[0], seat.scale[1], seat.scale[2]).multiplyScalar(it.scale);
      const q = new THREE.Quaternion().setFromEuler(new THREE.Euler(seat.euler[0], seat.euler[1], seat.euler[2]));
      const axis = new THREE.Vector3(seat.axis[0], seat.axis[1], seat.axis[2]);
      this.rocks.push({ x: it.pos[0], y: it.pos[1], z: it.pos[2], s, q, axis, rate: seat.rate });
      this.centre.add(this._p.set(it.pos[0], it.pos[1], it.pos[2]));
    });
    if (this.rocks.length) this.centre.divideScalar(this.rocks.length);
    for (const r of this.rocks) this.radius = Math.max(this.radius, Math.hypot(r.x - this.centre.x, r.y - this.centre.y, r.z - this.centre.z) + Math.max(r.s.x, r.s.y, r.s.z) * 1.5);
    const shade = new THREE.Color();
    for (const p of parts) {
      pinStaticMotionVectors(p.mat);
      const mesh = new THREE.InstancedMesh(p.geo, p.mat, Math.max(1, this.rocks.length));
      mesh.count = this.rocks.length;
      mesh.name = 'rock-instances';
      this.rocks.forEach((r, i) => {
        const g = 0.62 + h01(`${key}:${i}:shade`) * 0.5;
        mesh.setColorAt(i, shade.setRGB(g, g * (0.96 + h01(`${key}:${i}:warm`) * 0.06), g * 0.94));
      });
      if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
      this.group.add(mesh);
      this.meshes.push({ mesh, local: p.local });
    }
    this.write(off);
    rockFieldState.fields++; rockFieldState.rocks += this.rocks.length; rockFieldState.meshes += this.meshes.length;
  }

  /** Rewrite every instance from ABSOLUTE positions (the mesh held at the root's origin). */
  private write(off: THREE.Vector3): void {
    // The GROUP is the root's child the floating-origin rebase shifts; a rewrite from absolute − off resets it.
    this.group.position.set(0, 0, 0);
    for (const { mesh, local } of this.meshes) {
      mesh.position.set(0, 0, 0);
      this.rocks.forEach((r, i) => {
        this._r.compose(this._p.set(r.x - off.x, r.y - off.y, r.z - off.z), r.q, r.s);
        mesh.setMatrixAt(i, this._m.multiplyMatrices(this._r, local));
      });
      mesh.instanceMatrix.needsUpdate = true;
      if (!mesh.boundingSphere) mesh.boundingSphere = new THREE.Sphere();
      mesh.boundingSphere.center.copy(this.centre).sub(off);
      mesh.boundingSphere.radius = this.radius;
    }
  }

  /** Per frame: a near field tumbles (matrices from absolute − off); a far one rides the rebase untouched. */
  update(dt: number, shipAbs: THREE.Vector3, off: THREE.Vector3): boolean {
    if (this.centre.distanceTo(shipAbs) - this.radius > SPIN_M) return false;
    for (const r of this.rocks) r.q.premultiply(this._dq.setFromAxisAngle(r.axis, r.rate * dt));
    this.write(off);
    return true;
  }

  /** The field's centre (ABSOLUTE) and reach — for the caller's tests. */
  get bounds(): { centre: THREE.Vector3; radius: number; rocks: number } { return { centre: this.centre, radius: this.radius, rocks: this.rocks.length }; }
}
